import { useTranslation } from 'react-i18next';
import React, { useMemo, useCallback, useState } from 'react';
import cx from 'clsx';

import { IconRcAdd, IconRcCross } from '@/assets/common';
import { SUPPORTED_LOCALE_TYPE } from '@/locales';
import { Modal } from '@/components';
import { RichTextData, RichTextEditor, RichTextEditorHandle } from '@/components/RichTextEditor';
import { useSwitch } from '@/hooks';
import { devLog } from '@/utils';

import { PostFooter } from '../PostFooter';
import style from './index.module.scss';

export type AnswerProps = {
  onAnswerConfirm: (data: { thread_data: EditorSubmitContentData['thread_data'] }) => Promise<void>;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  language?: SUPPORTED_LOCALE_TYPE;
  hideChannelPicker?: boolean;
};

export const Answer = ({
  onAnswerConfirm,
  disabled = false,
  className,
  placeholder,
  language,
  hideChannelPicker,
}: AnswerProps) => {
  const { t } = useTranslation();

  const [editors, setEditors] = useState<
    Array<{
      editor: RichTextEditorHandle | null;
      id: number;
      invalid: boolean;
    }>
  >([
    {
      editor: null,
      id: Math.random(),
      invalid: true,
    },
    {
      editor: null,
      id: Math.random(),
      invalid: true,
    },
  ]);
  const { on: isLoading, turn: setLoading } = useSwitch();
  const { on: isUploading, turn: setImageUploading } = useSwitch();
  const [currentEditorIdx, setCurrentEditorIdx] = useState(0);

  const editorsLength = editors.length;

  const handleTabClick = useCallback(({ currentTarget }: React.MouseEvent<HTMLDivElement>) => {
    const { idx } = currentTarget.dataset;

    setCurrentEditorIdx(Number(idx));
  }, []);

  const handleAddClick = useCallback(() => {
    setEditors(pre =>
      pre.concat({
        editor: null,
        id: Math.random(),
        invalid: true,
      }),
    );

    if (currentEditorIdx + 1 === editorsLength) {
      setCurrentEditorIdx(currentEditorIdx + 1);
    }
  }, [currentEditorIdx, editorsLength]);

  const handleDeleteClick = useCallback(
    async (e: React.MouseEvent<SVGElement>) => {
      e.stopPropagation();
      const { currentTarget } = e;
      const { idx } = currentTarget.dataset;
      const nIdx = Number(idx);

      try {
        if (!editors[nIdx].invalid) {
          await Modal.confirm({
            title: `${t('Qa_Answer.xskn2j')} ${nIdx + 1}/${editorsLength}`,
            content: t('Qa_Answer.c8tgk4'),
            confirmBtnText: t('Qa_Answer.xskn2j'),
          });
        }

        setEditors(pre => pre.filter((_, i) => i !== nIdx));

        if (currentEditorIdx >= nIdx) {
          setCurrentEditorIdx(pre => pre - 1);
        }
      } catch (error) {}
    },
    [editors, currentEditorIdx, editorsLength, t],
  );

  const handleDisabledChange = useCallback(
    (isDisabled: boolean, i: number) => {
      if (editors[i].invalid !== isDisabled) {
        const next = [...editors];
        next[i] = {
          ...next[i],
          invalid: isDisabled,
        };
        setEditors(next);
      }
    },
    [editors],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const textDatas = await Promise.all(editors.map(x => x?.editor?.getSubmitContent()));
      const validTextDatas: RichTextData[] = [];

      const emptyTextPages = textDatas
        .reduce((s, d, i) => {
          if (d?.textContent || d?.image_ids?.length) {
            validTextDatas.push(textDatas[i]!);
            return s;
          }

          s.push(`Page ${i + 1} / ${editorsLength}`);
          return s;
        }, [] as string[])
        .join('，');

      if (emptyTextPages) {
        await Modal.confirm({
          title: t('Qa_Answer.vpyg15'),
          content: t('Qa_Answer.emptyTipContent', { pages: emptyTextPages }),
          cancelBtnText: t('Qa_Answer.go-edit'),
          confirmBtnText: t('Qa_Answer.x8bj0w'),
          cancelBtnType: 'primary',
        });
      }

      if (!validTextDatas.length) {
        return;
      }

      setLoading(true);
      const threads = validTextDatas.map(x => ({
        content: x.textContent,
        entities: {
          ...(x.mentionsList.length ? { mentions: x.mentionsList } : {}),
          ...(x.hashtagsList.length ? { tags: x.hashtagsList } : {}),
          ...(x.urlsWidthIndices.length ? { url: x.urlsWidthIndices } : {}),
          ...(x.slashesList.length ? { channels: x.slashesList } : {}),
        },
        image_ids: x.image_ids,
        card_id: x.card_id,
      }));

      const params = { thread_data: threads };

      devLog('submit qa answer content', params);
      await onAnswerConfirm?.(params);
    } finally {
      setLoading(false);
    }
  }, [editors, editorsLength, onAnswerConfirm, setLoading, t]);

  const isInValid = useMemo(() => !editors.length || editors.every(x => x.invalid), [editors]);

  return (
    <div className={cx(style.answerContainer, className)}>
      <div className={style.main}>
        <div className={style.tabs}>
          {editors.map((_, i) => (
            <div
              className={cx(
                style.tab,
                i > 1 && style.hasCrossIcon,
                i === currentEditorIdx && style.isSelected,
              )}
              key={_.id}
              onClick={handleTabClick}
              data-idx={i}>
              {i + 1} / {editorsLength}
              {i > 1 ? (
                <IconRcCross className={style.crossIcon} onClick={handleDeleteClick} data-idx={i} />
              ) : null}
            </div>
          ))}
          {!disabled && editorsLength < 10 ? (
            <IconRcAdd className={style.addIcon} onClick={handleAddClick} />
          ) : null}
        </div>
        {editors.map((x, i) => (
          <RichTextEditor
            maxLength={600}
            language={language}
            editorClassName={style.textEditor}
            placeholderClassName={style.editorStyle}
            wrapperClassName={cx(style.editorWrapper, currentEditorIdx === i && style.isShown)}
            footer={false}
            onInvalidChange={isDisabled => handleDisabledChange(isDisabled, i)}
            placeholder={
              i
                ? t('Qa_Answer.holderMore')
                : !!placeholder
                  ? placeholder
                  : t('Qa_Answer.holderCommon')
            }
            disabled={disabled}
            autoFocus
            toolbar={!disabled}
            key={editors[i].id}
            ref={r => (editors[i].editor = r)}
            onImageUploading={setImageUploading}
          />
        ))}
      </div>
      <PostFooter
        disabled={disabled || isUploading || isInValid}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        showPaidEligibility
        hideChannelPicker={hideChannelPicker}
      />
    </div>
  );
};
